import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navLinks = [
  { to: "/", label: "首页", end: true },
  { to: "/category/movie", label: "电影" },
  { to: "/category/series", label: "电视剧" },
  { to: "/review/topic/t1", label: "影评专题" },
  { to: "/about", label: "关于" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const transparent = isHome && !scrolled && !menuOpen;

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
        transparent
          ? "border-transparent bg-transparent"
          : "border-background-200 bg-background-50/95 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-[72px] items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-2.5">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-md ${
              transparent ? "bg-background-50/15 text-background-50" : "bg-primary-600 text-background-50"
            }`}
          >
            <i className="ri-film-line text-xl" />
          </span>
          <span
            className={`font-heading text-lg font-black tracking-wide md:text-xl ${
              transparent ? "text-background-50" : "text-foreground-950"
            }`}
          >
            疯狂的欲望
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) =>
                `cursor-pointer whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? transparent
                      ? "bg-background-50/15 text-background-50"
                      : "bg-primary-50 text-primary-700"
                    : transparent
                      ? "text-background-100/90 hover:bg-background-50/10 hover:text-background-50"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={handleSearch} className="ml-auto hidden items-center md:flex">
          <div className="relative">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="搜索影视、演员、影评"
              className={`w-40 rounded-full border py-2 pl-9 pr-4 text-sm outline-none transition-all focus:w-56 ${
                transparent
                  ? "border-background-50/30 bg-background-50/10 text-background-50 placeholder:text-background-100/70 focus:bg-background-50/20"
                  : "border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:bg-background-50"
              }`}
            />
          </div>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className={`ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md lg:hidden md:ml-0 ${
            transparent ? "text-background-50" : "text-foreground-900"
          }`}
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <div className="flex flex-col gap-1 px-4 py-4">
            <form onSubmit={handleSearch} className="mb-3">
              <div className="relative">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  type="text"
                  placeholder="搜索影视、演员、影评"
                  className="w-full rounded-full border border-background-300 bg-background-100 py-2.5 pl-9 pr-4 text-sm text-foreground-900 outline-none placeholder:text-foreground-500 focus:border-primary-400"
                />
              </div>
            </form>
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) =>
                  `cursor-pointer rounded-md px-3 py-2.5 text-sm font-medium ${
                    isActive ? "bg-primary-50 text-primary-700" : "text-foreground-700 hover:bg-background-100"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}